import {
    AmenitiesList,
    Button as BflButton,
    createOccupancyParameters,
    DateRangeSelectorInputs,
    getAvailabilityFetcher,
    getCalendarPrices,
    Icon,
    withAvailabilityData,
    withPriceData,
} from '@kognitiv/bf-library';
import moment from 'moment';
import React from 'react';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { getMissingMessage } from '../../../shared/utils/UtilsHelper';
import Button from 'Components/Card/Button';
import Card from 'Components/shared/Card';
import CardDescription from 'Components/shared/CardDescription';
import CardImage from 'Components/shared/CardImage';
import CardSpecs from 'Components/shared/CardSpecs';
import CardTitle from 'Components/shared/CardTitle';
import ConnectedCardPrice from 'Components/shared/ConnectedCardPrice';
import { layoutsDefaultProps, layoutsPropTypes } from 'Constants/PropTypes';
import { ECHO_WIDGET } from 'Constants/WidgetConfig';
import { URLService } from 'Services';
import { UrlTransformers } from 'Shared/urltransformer/UrlTransformers';

function checkInCheckOutReducer(state, action) {
    switch (action.type) {
        case 'SET_CALENDAR_CHECK_IN':
            return { ...state, calendarCheckIn: action.payload };
        case 'SET_CALENDAR_CHECK_OUT':
            return { ...state, calendarCheckOut: action.payload };
        case 'SET_CHECK_IN_BACKUP':
            return { ...state, checkInBackup: action.payload };
        case 'SET_CHECK_OUT_BACKUP':
            return { ...state, checkOutBackup: action.payload };
    }
}

function RoomCard(props) {
    const {
        title, description, images, isOffer, isLoading,
        maxOccupancy, price, code, currencyDisplay, currency,
        size, sizeUnit, showDescription, showSpecs, shouldShowLogin,
        onSearchResults, showTeaser, teaser, discount, originalPrice, intl,
        shouldUpgradeTier, language, shouldHideButton,
        country, availableDaterange, amenities, checkIn, checkOut, currentLanguage,
        minLengthOfStay, maxLengthOfStay, minNightOffset, maxCheckOutDate,
        isMultiProperty, calendarPricesMode, widgetType, defaultOccupancy,
        priceCalcMode, priceDisplayMode, channelId, propertyCode, minOccupancy,
        searchBarExists, domain, displayedMode,
        promotionalCodeEnabled, promotionalCode,
    } = props;

    const priceInfo = { price, originalPrice, discount };
    const [ initialStartDate ] = useState(checkIn ? moment(checkIn, 'YYYY-MM-DD') : null);
    const [ initialEndDate ] = useState(checkOut ? moment(checkOut, 'YYYY-MM-DD') : null);
    const isShowingPrices = (calendarPricesMode) => [ 'FAIR', 'CHEAPEST' ].includes(calendarPricesMode);
    const globalMinOccupancy = isMultiProperty ? 0 : minOccupancy;
    const [ isCalendarDisplayed, setIsCalendarDisplayed ] = useState(false);
    const [ selectedCheckInDate, setSelectedCheckInDate ] = useState(null);
    const [ selectedCheckOutDate, setSelectedCheckOutDate ] = useState(null);
    const [ checkInCheckOutState, checkInCheckOutDispatch ] = useReducer(
        checkInCheckOutReducer, {
            calendarCheckIn: initialStartDate,
            calendarCheckOut: initialEndDate,
            checkInBackup: initialStartDate,
            checkOutBackup: initialEndDate,
        });

    let buttonLabel;

    if (shouldUpgradeTier) {
        // i18nLabel[bf.upgradeTier]
        buttonLabel = getMissingMessage('bf.upgradeTier');
    } else if (price === null) {
        buttonLabel = intl.formatMessage({ id: 'bf.checkAvailability', defaultMessage: 'bf.checkAvailability' });
    }

    const shouldNotShowWarning = !shouldUpgradeTier && !shouldHideButton && (price !== null && price > 0);

    if (shouldNotShowWarning) {
        if (isOffer) {
            // i18nLabel[bf.details]
            buttonLabel = getMissingMessage('bf.details');
        }

        // i18nLabel[bf.bookNow]
        buttonLabel = getMissingMessage('bf.bookNow');

        if (availableDaterange) {
            // i18nLabel[bf.viewDetails]
            buttonLabel = getMissingMessage('bf.viewDetails');
        }
    }

    let occupancy = '2';

    if (defaultOccupancy?.length > 0) {
        const occupancyData = defaultOccupancy[0];
        const childToken = occupancyData.children.map(child => child === -1 ? 0 : child).join(':');

        occupancy = childToken
            ? `${occupancyData.adults}:${childToken}`
            : `${occupancyData.adults}`;
    }

    const repoId = `cal-${calendarPricesMode}-${priceCalcMode}-${currency.code}-${code}-${occupancy}`;

    const calculateCalendarPriceMode = (isMultiProperty, widgetType, calendarPricesMode) =>
        isMultiProperty || widgetType === ECHO_WIDGET || !calendarPricesMode
            ? 'NONE'
            : calendarPricesMode;

    const sanitizeCalendarPricesMode = (mode) => [ 'CHEAPEST', 'FAIR', 'NONE' ].includes(mode)
        ? mode
        : 'NONE';

    const CalendarComponent = useMemo(() => {
        if (isMultiProperty) {
            return DateRangeSelectorInputs;
        }

        return isShowingPrices(calendarPricesMode)
            ? withPriceData(DateRangeSelectorInputs)
            : withAvailabilityData(DateRangeSelectorInputs);
    }, [ isMultiProperty, calendarPricesMode ]);

    const getFetch = ({
        isMultiProperty, occupancy, calendarPricesMode, currencyCode, propertyCode, globalMinOccupancy, priceCalcMode, channelId,
    }) => {
        if (isMultiProperty) {
            return null;
        }

        if (isShowingPrices(calendarPricesMode)) {
            const url = URLService.externals.getCalendarPricesURL();
            const calculateTaxes = !priceCalcMode ||
                [ 'withTaxesAndFees', 'withInformativeTaxesAndFees' ].includes(priceCalcMode);
            const includeInformativeTaxesAndFees = !priceCalcMode ||
                [ 'asInPricelist', 'withInformativeTaxesAndFees' ].includes(priceCalcMode);

            const params = {
                url,
                currencyCode,
                occupancy,
                propertyCode,
                includeInformativeTaxesAndFees,
                calculateTaxes,
                channelID: channelId,
                priceMode: calendarPricesMode,
            };

            if (isOffer) {
                params.ratePlanCode = code;
            } else {
                params.roomCode = code;
            }

            return getCalendarPrices(params);
        } else {
            const url = URLService.externals.getAvailabilitiesURL(propertyCode);

            return getAvailabilityFetcher({
                url,
                occupancy,
                globalMinOccupancy,
            });
        }
    };

    const calendarPriceMode = calculateCalendarPriceMode(isMultiProperty, widgetType, calendarPricesMode);
    const priceMode = sanitizeCalendarPricesMode(calendarPricesMode);

    const onDatesChanged = (dates) => {
        checkInCheckOutDispatch({ type: 'SET_CHECK_IN_BACKUP', payload: dates?.startDate });
        checkInCheckOutDispatch({ type: 'SET_CHECK_OUT_BACKUP', payload: dates?.endDate });

        if (dates?.startDate && dates?.endDate) {
            const formattedCheckin = typeof dates.startDate === 'string'
                ? dates.startDate
                : dates.startDate.format('YYYY-MM-DD');
            const formattedCheckout = typeof dates.endDate === 'string'
                ? dates.endDate
                : dates.endDate.format('YYYY-MM-DD');

            setSelectedCheckInDate(formattedCheckin);
            setSelectedCheckOutDate(formattedCheckout);
        }
    };

    useEffect(() => {
        if (isCalendarDisplayed) {
            if (checkInCheckOutState.checkInBackup && checkInCheckOutState.checkOutBackup) {
                checkInCheckOutDispatch({
                    type: 'SET_CALENDAR_CHECK_IN', payload: checkInCheckOutState.checkInBackup.format('YYYY-MM-DD'),
                });
                checkInCheckOutDispatch({
                    type: 'SET_CALENDAR_CHECK_OUT', payload: checkInCheckOutState.checkOutBackup.format('YYYY-MM-DD'),
                });
            } else {
                checkInCheckOutDispatch({ type: 'SET_CALENDAR_CHECK_IN', payload: null });
                checkInCheckOutDispatch({ type: 'SET_CALENDAR_CHECK_OUT', payload: null });
            }
        }

        // ignore checkInBackup and checkOutBackup changes, as calenderCheckIn and calenderCheckOut
        // need to be updated only when calendar is going to be displayed, after it was hidden
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ isCalendarDisplayed ]);

    const goToKubeSearchResults = () => {
        onSearchResults(code, availableDaterange, selectedCheckInDate, selectedCheckOutDate);
    };

    const onGoToKubeLandingPage = () => {
        const occupanciesString = createOccupancyParameters(defaultOccupancy);

        const propertyCodeParam = isMultiProperty ? `/property/${propertyCode}` : '';
        const roomCodeParam = `/${isOffer ? 'package' : 'room'}/${code}`;

        const occupanciesParam = occupanciesString ? `&${occupanciesString}` : '';
        const currencyCodeParam = `&skd-currency-code=${currency.code}`;
        const priceCalcModeParam = `&skd-price-type=${priceCalcMode}`;
        const priceDisplayModeParam = `&skd-time-price-base=${priceDisplayMode}`;
        const promotionalCodeParam = promotionalCodeEnabled && promotionalCode ? `&skd-promotion-code=${promotionalCode}` : '';
        const languageParam = `&skd-language-code=${language}`;

        const url = `https://${domain}${propertyCodeParam}${roomCodeParam}?` +
            `${occupanciesParam}${currencyCodeParam}${priceCalcModeParam}${priceDisplayModeParam}${promotionalCodeParam}${languageParam}`;

        UrlTransformers.instance().transform(url).then(transformedUrl => window.open(transformedUrl, '_blank'));
    };

    const onFetch = getFetch({
        isMultiProperty,
        channelId,
        occupancy,
        priceMode,
        propertyCode,
        currencyCode: currency.code,
        currentRoom: code,
        calendarPricesMode: calendarPriceMode,
        globalMinOccupancy,
        priceCalcMode,
    });

    return (
        <Card isLoading={isLoading} className="card-custom-class" id={isOffer ? null : `room-card-${code}`}>
            <div className={`kbe-room-card ${isOffer ? 'kbe-offer-card' : ''}`}>
                <CardImage
                    images={images}
                    title={title}
                    code={code}
                    className="kbe-room-card-image"
                />
                <div className="kbe-room-card-main-info-wrapper">

                    <CardTitle className="kbe-room-card-title">
                        {title}
                    </CardTitle>
                    {
                        showSpecs ? <CardSpecs
                            className="kbe-room-card-specs"
                            maxOccupancy={maxOccupancy}
                            size={size}
                            sizeUnit={sizeUnit}
                            language={language}
                        /> : null
                    }
                    {
                        amenities && amenities.length > 0 ? <AmenitiesList
                            amenities={amenities}
                            iconSize="medium"
                            maxQuantity={8}
                            labels={{
                                showMore: <FormattedMessage id="bf.showMore" defaultMessage="bf.showMore" />,
                                showLess: <FormattedMessage id="bf.showLess" defaultMessage="bf.showLess" />,
                                showAll: <FormattedMessage id="bf.showAllAmenities" defaultMessage="bf.showAllAmenities" />,
                                amenities: <FormattedMessage id="bf.amenities" defaultMessage="bf.amenities" />,
                            }}

                        /> : null
                    }
                    {
                        showDescription ? <CardDescription
                            className="kbe-room-card-description"
                            content={description}
                            language={language}
                        /> : null
                    }
                    {
                        showTeaser ? <CardDescription
                            className="kbe-room-card-teaser"
                            content={teaser}
                            language={language}
                        /> : null
                    }
                </div>
                <div className="kbe-room-card-price-and-selection-wrapper">
                    <ConnectedCardPrice
                        language={language}
                        priceInfo={priceInfo}
                        currency={currency}
                        currencyDisplay={currencyDisplay}
                        country={country}
                        availableDaterange={availableDaterange}
                        className="kbe-room-card-price"
                    />
                    {
                        !shouldHideButton
                        && (
                            <Button
                                isOffer={isOffer}
                                onClick={() => goToKubeSearchResults()}
                                className="kbe-room-card-selection-button"
                                // i18nLabel[bf.logIn]
                                content={shouldShowLogin
                                    ? getMissingMessage('bf.logIn')
                                    : buttonLabel}
                                priceInfo={priceInfo.price}
                                currency={currency.code}
                            />
                        )
                    }
                    {
                        !searchBarExists && displayedMode === 'show-as-list'
                        && (
                            <div className="kbe-room-card-calendar-buttons">
                                <BflButton
                                    className="show-calendar-toggle"
                                    transparent
                                    size="small"
                                    content={<>
                                        <Icon
                                            className="room-card-calendar-icon"
                                            name="bf-icon-calendar-day"
                                        />
                                        { isCalendarDisplayed
                                            ? <FormattedMessage id="bf.hideRoomCalendar" defaultMessage="bf.hideRoomCalendar" />
                                            : <FormattedMessage id="bf.showRoomCalendar" defaultMessage="bf.showRoomCalendar" /> }
                                    </>}
                                    onClick={() => setIsCalendarDisplayed(!isCalendarDisplayed)}
                                />
                            </div>
                        )
                    }
                    {
                        !searchBarExists && displayedMode === 'show-as-grid'
                        && (
                            <div className="kbe-room-card-calendar-buttons">
                                <BflButton
                                    className="show-calendar-toggle"
                                    transparent
                                    size="small"
                                    content={<FormattedMessage id="bf.seeCalendar" defaultMessage="bf.seeCalendar" />}
                                    onClick={onGoToKubeLandingPage}
                                />
                            </div>
                        )
                    }
                </div>
                {isCalendarDisplayed ?
                    <div className="kbe-room-card-calendar-wrapper">
                        <CalendarComponent
                            showInputs={false}
                            datesChanged={onDatesChanged}
                            isAlwaysOpen={true}
                            isCalendarOpen={true}
                            isVisible={isCalendarDisplayed}
                            currentLanguage={currentLanguage}
                            checkIn={checkInCheckOutState.calendarCheckIn}
                            checkOut={checkInCheckOutState.calendarCheckOut}
                            initialStartDate={initialStartDate}
                            initialEndDate={initialEndDate}
                            maxLengthOfStay={maxLengthOfStay}
                            minLengthOfStay={minLengthOfStay}
                            minNightOffset={minNightOffset}
                            onChange={goToKubeSearchResults}
                            maxCheckOutDate={maxCheckOutDate}
                            isMultiProperty={isMultiProperty}
                            calendarPricesMode={priceMode}
                            occupancy={occupancy}
                            propertyCode={propertyCode}
                            fetch={onFetch}
                            repoIdentifier={repoId}
                            currencyDisplay={currencyDisplay}
                            currencyCode={currency.code}
                            currencySymbol={currency.symbol}
                            showCancelButton={false}
                            preventDateSelectionOnOutsideClick={true}
                            intl={intl}
                        />
                    </div>
                    : null}

            </div>
        </Card>
    );
}

RoomCard.defaultProps = layoutsDefaultProps;
RoomCard.propTypes = layoutsPropTypes;

export default injectIntl(RoomCard);
